/*
 * Billing helpers shared by the app shells and the Plan & Credits tab.
 *
 * Stripe Checkout returns to the app origin with a SERVER query string
 * (`/?billing=success&ws=123&session_id=…`). The router runs in hash mode, so that query survives
 * every in-app navigation untouched — which is exactly why nothing reacted to it: the only code
 * that could confirm the outcome lived in the Plan & Credits tab, and that tab mounts only when
 * someone opens the workspace modal by hand. The shells now read the query once their workspace
 * has loaded and open that tab themselves (see openBillingReturn in Client / Lite / Builder).
 *
 * The billing alert emails use the same road (`/?billing=open&to=credits&ws=123`): their buttons
 * only say which page to open, so there is nothing to confirm and the shell clears the query as
 * soon as it has read it.
 *
 * AutoCount Full (docs/billing-autocount-connector-pricing-proposal.md §6.3 in the SVN repo) adds
 * `connector=autocount` to its own Checkout's return (`/?billing=success&ws=123&connector=autocount
 * &session_id=…`, or `billing=cancel`), and its trial emails open `to=autocount`. Both land on
 * Settings › AutoCount, which confirms the purchase itself.
 */

const CURRENCY_SYMBOLS = { usd: "US$", sgd: "S$", myr: "RM" };

/**
 * "RM229", "US$59", "S$1,450", "RM49.90" — whole amounts drop the cents, the way a price list reads.
 * Empty for a missing or non-positive amount so a template can `v-if` on it directly.
 */
export function formatMoney(amount, currency) {
  const n = Number(amount);
  if (!isFinite(n) || n <= 0) return "";
  const code = String(currency || "usd").toLowerCase();
  const symbol = Object.prototype.hasOwnProperty.call(CURRENCY_SYMBOLS, code)
    ? CURRENCY_SYMBOLS[code]
    : code.toUpperCase() + " ";
  const whole = Math.abs(n - Math.round(n)) < 0.005;
  const digits = whole ? 0 : 2;
  return symbol + n.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** The settings pages a billing email may open; anything else opens Billing & invoices. */
const OPEN_TARGETS = ["billing", "credits", "plan", "autocount"];

/** The connectors whose Checkout returns name them; each has its own settings page. */
const CONNECTOR_SECTIONS = { autocount: "autocount" };

/**
 * The Stripe return carried by a query string, or null when there is none:
 * { kind: "success" | "pack" | "cancel", workspaceId: number | null, sessionId: string | null }.
 * An email button's `open` also carries `to`, one of OPEN_TARGETS; a connector's Checkout return
 * also carries `connector` ("autocount"). `kind` is only ever one of the values the server puts in
 * its links.
 */
export function readBillingReturn(search) {
  const params = parseQuery(search);
  const kind = params.billing;
  if (kind !== "success" && kind !== "pack" && kind !== "cancel" && kind !== "open") return null;
  const ws = parseInt(params.ws, 10);
  const ret = {
    kind,
    workspaceId: isFinite(ws) && ws > 0 ? ws : null,
    sessionId: params.session_id || null,
  };
  if (kind === "open") ret.to = OPEN_TARGETS.indexOf(params.to) >= 0 ? params.to : "billing";
  else if (Object.prototype.hasOwnProperty.call(CONNECTOR_SECTIONS, params.connector)) ret.connector = params.connector;
  return ret;
}

/**
 * The settings page a return opens, for the shells' settingsSectionFor: a connector's own page
 * after its Checkout, the page an email names, or nothing (the Plan page) for a plan or pack.
 */
export function billingReturnSection(ret) {
  if (!ret) return undefined;
  if (ret.connector && CONNECTOR_SECTIONS[ret.connector]) return CONNECTOR_SECTIONS[ret.connector];
  return ret.to;
}

/**
 * The same URL with the Stripe return parameters removed and everything else — other query
 * pairs, the hash route — left exactly as it was. Pure; the caller replaces history.
 */
export function stripBillingReturn(href) {
  const text = String(href || "");
  const hashAt = text.indexOf("#");
  const beforeHash = hashAt >= 0 ? text.slice(0, hashAt) : text;
  const hash = hashAt >= 0 ? text.slice(hashAt) : "";
  const queryAt = beforeHash.indexOf("?");
  if (queryAt < 0) return text;
  const base = beforeHash.slice(0, queryAt);
  const pairs = beforeHash.slice(queryAt + 1).split("&");
  // `to` and `connector` are common names, so each goes only when it came with a billing return:
  // `to` with an email's `billing=open`, `connector` with a Checkout's.
  const opening = pairs.indexOf("billing=open") >= 0;
  const returning = pairs.some((pair) => /^billing=(success|pack|cancel)$/.test(pair));
  const kept = pairs.filter(
    (pair) => pair && !/^(billing|ws|session_id)=/.test(pair)
      && !(opening && /^to=/.test(pair))
      && !(returning && /^connector=/.test(pair))
  );
  return base + (kept.length ? "?" + kept.join("&") : "") + hash;
}

/** Takes the billing query out of the address bar, keeping the hash route. Cosmetic; never throws. */
export function clearBillingReturn() {
  try {
    window.history.replaceState(window.history.state, "", stripBillingReturn(window.location.href));
  } catch (e) { /* cosmetic only */ }
}

function parseQuery(search) {
  const out = {};
  const text = String(search || "");
  const start = text.indexOf("?");
  const query = start >= 0 ? text.slice(start + 1) : text;
  query.split("&").forEach((pair) => {
    if (!pair) return;
    const eq = pair.indexOf("=");
    let key;
    let value;
    try {
      key = decodeURIComponent(eq >= 0 ? pair.slice(0, eq) : pair);
      value = eq >= 0 ? decodeURIComponent(pair.slice(eq + 1).replace(/\+/g, " ")) : "";
    } catch (e) {
      return; // malformed escape — not ours, skip it
    }
    if (!Object.prototype.hasOwnProperty.call(out, key)) out[key] = value;
  });
  return out;
}
